/* Chef Nouel Catis — Maison theme scripts */
(() => {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const theme = window.theme || { routes: {}, strings: {} };

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const formatMoney = (cents, format = theme.moneyFormat || '{{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) / 100;
    const fmt = (n, decimals, thousands = ',', decimal = '.') => {
      const parts = n.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimals ? decimal : '');
    };
    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    const key = match ? match[1] : 'amount';
    let out;
    switch (key) {
      case 'amount_no_decimals': out = fmt(value, 0); break;
      case 'amount_with_comma_separator': out = fmt(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = fmt(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = fmt(value, 2, "'", '.'); break;
      default: out = fmt(value, 2);
    }
    return format.replace(/\{\{\s*\w+\s*\}\}/, out);
  };

  /* ---------- Toast ---------- */
  const toast = (message) => {
    const el = $('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._t);
    el._t = setTimeout(() => el.classList.remove('is-visible'), 3200);
  };

  /* ---------- Focus trap helpers ---------- */
  const focusable = (ctx) =>
    $$('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])', ctx).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );

  const trapFocus = (container, e) => {
    if (e.key !== 'Tab') return;
    const items = focusable(container);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  /* ---------- Drawers (menu, cart, filters) ---------- */
  const Drawers = {
    active: null,
    lastTrigger: null,
    open(id, trigger) {
      const drawer = document.getElementById(id);
      if (!drawer) return;
      if (this.active && this.active !== drawer) this.close(false);
      this.lastTrigger = trigger || document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      $$(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
      this.active = drawer;
      const target = $('[data-autofocus]', drawer) || $('.drawer__panel button, .drawer__panel a', drawer);
      setTimeout(() => target && target.focus({ preventScroll: true }), 50);
    },
    close(restoreFocus = true) {
      const drawer = this.active;
      if (!drawer) return;
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      $$(`[aria-controls="${drawer.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.active = null;
      if (restoreFocus && this.lastTrigger) this.lastTrigger.focus({ preventScroll: true });
    },
    init() {
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-drawer-open]');
        if (opener) {
          e.preventDefault();
          this.open(opener.dataset.drawerOpen, opener);
          return;
        }
        if (e.target.closest('[data-drawer-close]')) {
          e.preventDefault();
          this.close();
        }
      });
      document.addEventListener('keydown', (e) => {
        if (!this.active) return;
        if (e.key === 'Escape') this.close();
        else trapFocus($('.drawer__panel', this.active), e);
      });
    }
  };

  /* ---------- Announcement bar ---------- */
  const initAnnouncements = () => {
    $$('[data-announcement]').forEach((bar) => {
      const items = $$('.announcement__item', bar);
      if (items.length < 2 || reducedMotion) return;
      let i = 0;
      const speed = Number(bar.dataset.speed || 5) * 1000;
      setInterval(() => {
        items[i].classList.remove('is-active');
        i = (i + 1) % items.length;
        items[i].classList.add('is-active');
      }, speed);
    });
  };

  /* ---------- Sticky / transparent header ---------- */
  const initHeader = () => {
    const wrapper = $('[data-header-wrapper]');
    if (!wrapper) return;
    const header = $('.site-header', wrapper);
    const hideOnScroll = wrapper.dataset.hideOnScroll === 'true';
    let lastY = window.scrollY;
    let ticking = false;
    const setHeight = () => document.documentElement.style.setProperty('--header-h', `${header.offsetHeight}px`);
    setHeight();
    window.addEventListener('resize', debounce(setHeight, 150));
    const update = () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 10);
      if (hideOnScroll && wrapper.classList.contains('is-sticky')) {
        const goingDown = y > lastY && y > 300;
        wrapper.classList.toggle('is-hidden', goingDown && !header.matches(':focus-within'));
      }
      lastY = y;
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  };

  /* ---------- Generic fader (hero slides, quotes) ---------- */
  class Fader {
    constructor(root, { slide, dot, prev, next, counter }) {
      this.root = root;
      this.slides = $$(slide, root);
      this.dots = dot ? $$(dot, root) : [];
      this.prevBtn = prev ? $(prev, root) : null;
      this.nextBtn = next ? $(next, root) : null;
      this.counter = counter ? $(counter, root) : null;
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.autoplay = root.dataset.autoplay === 'true' && !reducedMotion;
      this.speed = Number(root.dataset.speed || 6) * 1000;
      if (this.slides.length < 2) return;
      root.style.setProperty('--autoplay', `${this.speed / 1000}s`);
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i, true)));
      this.prevBtn && this.prevBtn.addEventListener('click', () => this.go(this.index - 1, true));
      this.nextBtn && this.nextBtn.addEventListener('click', () => this.go(this.index + 1, true));
      root.addEventListener('mouseenter', () => this.pause());
      root.addEventListener('mouseleave', () => this.play());
      root.addEventListener('focusin', () => this.pause());
      root.addEventListener('focusout', () => this.play());
      this.bindSwipe();
      this.go(this.index);
      this.play();
    }
    go(i, user = false) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.slides.forEach((s, idx) => {
        const active = idx === this.index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', String(!active));
        $$('a, button', s).forEach((el) => (active ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
      });
      this.dots.forEach((d, idx) => {
        // restart progress animation
        d.setAttribute('aria-current', 'false');
        if (idx === this.index) { void d.offsetWidth; d.setAttribute('aria-current', 'true'); }
      });
      if (this.counter) this.counter.textContent = `${String(this.index + 1).padStart(2, '0')} / ${String(n).padStart(2, '0')}`;
      if (user) this.restart();
    }
    play() {
      if (!this.autoplay || this.timer) return;
      this.root.classList.remove('is-paused');
      this.timer = setInterval(() => this.go(this.index + 1), this.speed);
    }
    pause() {
      clearInterval(this.timer);
      this.timer = null;
      this.root.classList.add('is-paused');
    }
    restart() { this.pause(); this.play(); }
    bindSwipe() {
      let x0 = null;
      this.root.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
      this.root.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
        x0 = null;
      });
    }
  }

  const initFaders = (ctx = document) => {
    $$('[data-slideshow]', ctx).forEach((el) => {
      if (el._fader) return;
      el._fader = new Fader(el, { slide: '.hero__slide', dot: '.hero__dot', prev: '[data-prev]', next: '[data-next]', counter: '.hero__counter' });
    });
    $$('[data-quotes]', ctx).forEach((el) => {
      if (el._fader) return;
      el._fader = new Fader(el, { slide: '.quote', dot: '.quotes__dot' });
    });
  };

  /* ---------- Reveal on scroll ---------- */
  const initReveal = (ctx = document) => {
    const els = $$('.reveal:not(.is-visible)', ctx);
    if (!document.body.classList.contains('has-reveal') || !('IntersectionObserver' in window) || reducedMotion) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  };

  /* ---------- Cart ---------- */
  const Cart = {
    sectionIds() {
      const ids = new Set(['cart-icon-bubble']);
      $$('[data-cart-region]').forEach((el) => ids.add(el.dataset.cartRegion));
      return Array.from(ids);
    },
    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      return data;
    },
    render(sections) {
      if (!sections) return;
      const parser = new DOMParser();
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parser.parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`[data-cart-region="${id}"]`);
        $$(`[data-cart-region="${id}"]`).forEach((el) => {
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },
    async add(items) {
      const data = await this.request(`${theme.routes.cartAdd}.js`, { items });
      this.render(data.sections);
      if (theme.cartType === 'drawer' && document.getElementById('CartDrawer')) {
        Drawers.open('CartDrawer');
      } else if (theme.cartType === 'page') {
        window.location.href = theme.routes.cart;
      } else {
        toast(theme.strings.added);
      }
      return data;
    },
    async change(line, quantity) {
      const regions = $$('[data-cart-region]');
      regions.forEach((r) => r.closest('.cart-drawer, [data-cart-page]')?.classList.add('is-loading'));
      try {
        const data = await this.request(`${theme.routes.cartChange}.js`, { line, quantity });
        this.render(data.sections);
      } catch (err) {
        toast(err.message);
      } finally {
        regions.forEach((r) => r.closest('.cart-drawer, [data-cart-page]')?.classList.remove('is-loading'));
      }
    },
    updateNote: debounce((note) => {
      fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });
    }, 500),
    init() {
      document.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) {
          e.preventDefault();
          this.change(Number(remove.dataset.line), 0);
          return;
        }
        const qtyBtn = e.target.closest('[data-cart-qty] button');
        if (qtyBtn) {
          const wrap = qtyBtn.closest('[data-cart-qty]');
          const input = $('input', wrap);
          const next = Math.max(0, Number(input.value) + (qtyBtn.name === 'plus' ? 1 : -1));
          input.value = next;
          this.change(Number(wrap.dataset.line), next);
        }
      });
      document.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty] input');
        if (input) {
          const wrap = input.closest('[data-cart-qty]');
          this.change(Number(wrap.dataset.line), Math.max(0, Number(input.value)));
        }
      });
      document.addEventListener('input', (e) => {
        if (e.target.matches('[data-cart-note]')) this.updateNote(e.target.value);
      });
    }
  };

  /* ---------- Quantity selector (product page) ---------- */
  const initQuantity = () => {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-qty] button');
      if (!btn) return;
      const input = $('input', btn.closest('[data-qty]'));
      const min = Number(input.min || 1);
      const max = input.max ? Number(input.max) : Infinity;
      const step = Number(input.step || 1);
      const next = Number(input.value || min) + (btn.name === 'plus' ? step : -step);
      input.value = Math.min(max, Math.max(min, next));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  /* ---------- Product form ---------- */
  const initProductForms = (ctx = document) => {
    $$('form[data-product-form]', ctx).forEach((form) => {
      if (form._bound) return;
      form._bound = true;
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = $('[type="submit"]', form);
        const fd = new FormData(form);
        const item = { id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) };
        const props = {};
        for (const [k, v] of fd.entries()) {
          const m = k.match(/^properties\[(.+)\]$/);
          if (m && v) props[m[1]] = v;
        }
        if (Object.keys(props).length) item.properties = props;
        if (fd.get('selling_plan')) item.selling_plan = fd.get('selling_plan');
        btn && btn.classList.add('is-loading');
        try {
          await Cart.add([item]);
        } catch (err) {
          toast(err.message);
        } finally {
          btn && btn.classList.remove('is-loading');
        }
      });
    });

    // Quick add from product cards
    if (!document._quickAddBound) {
      document._quickAddBound = true;
      document.addEventListener('click', async (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn) return;
        e.preventDefault();
        btn.classList.add('is-loading');
        try {
          await Cart.add([{ id: Number(btn.dataset.quickAdd), quantity: 1 }]);
        } catch (err) {
          toast(err.message);
        } finally {
          btn.classList.remove('is-loading');
        }
      });
    }
  };

  /* ---------- Variant picker ---------- */
  const initVariantPickers = (ctx = document) => {
    $$('[data-product]', ctx).forEach((root) => {
      if (root._variantBound) return;
      root._variantBound = true;
      const jsonEl = $('[data-product-json]', root);
      if (!jsonEl) return;
      const product = JSON.parse(jsonEl.textContent);
      const picker = $('[data-variant-picker]', root);
      const idInput = $('form[data-product-form] input[name="id"]', root);
      const priceEl = $('[data-price]', root);
      const stickyPrice = $('[data-sticky-price]');
      const buttons = $$('[data-add-button]', root).concat($$('[data-sticky-add]'));
      const inventoryEl = $('[data-inventory]', root);

      const currentOptions = () =>
        $$('fieldset', picker).map((fs) => {
          const checked = $('input:checked', fs);
          return checked ? checked.value : null;
        });

      const updateAvailability = (selected) => {
        $$('fieldset', picker).forEach((fs, pos) => {
          $$('input', fs).forEach((input) => {
            const test = selected.slice();
            test[pos] = input.value;
            const match = product.variants.find((v) => v.options.every((o, i) => o === test[i]));
            input.classList.toggle('is-unavailable', !match || !match.available);
          });
          const label = $('legend [data-selected-value]', fs);
          if (label) label.textContent = selected[pos] || '';
        });
      };

      const renderPrice = (v) => {
        if (!priceEl) return;
        let html = '';
        if (v.compare_at_price && v.compare_at_price > v.price) {
          html = `<span class="price__sale">${formatMoney(v.price)}</span><s class="price__compare">${formatMoney(v.compare_at_price)}</s>`;
        } else {
          html = `<span>${formatMoney(v.price)}</span>`;
        }
        priceEl.innerHTML = html;
        if (stickyPrice) stickyPrice.textContent = formatMoney(v.price);
      };

      const onChange = () => {
        const selected = currentOptions();
        const variant = product.variants.find((v) => v.options.every((o, i) => o === selected[i]));
        updateAvailability(selected);
        buttons.forEach((b) => {
          const label = $('[data-add-label]', b) || b;
          if (!variant) {
            b.setAttribute('disabled', '');
            label.textContent = theme.strings.unavailable;
          } else if (!variant.available) {
            b.setAttribute('disabled', '');
            label.textContent = theme.strings.soldOut;
          } else {
            b.removeAttribute('disabled');
            label.textContent = theme.strings.addToCart;
          }
        });
        if (!variant) return;
        idInput.value = variant.id;
        $$('[data-sticky-variant]').forEach((i) => (i.value = variant.id));
        renderPrice(variant);
        if (inventoryEl) inventoryEl.hidden = !variant.available;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
        if (variant.featured_media) {
          const slide = $(`[data-media-id="${variant.featured_media.id}"]`, root);
          slide && root._gallery && root._gallery.goTo(slide);
        }
      };

      if (picker) {
        picker.addEventListener('change', onChange);
        updateAvailability(currentOptions());
      }
    });
  };

  /* ---------- Product gallery ---------- */
  const initGalleries = (ctx = document) => {
    $$('[data-gallery]', ctx).forEach((gallery) => {
      if (gallery._bound) return;
      gallery._bound = true;
      const main = $('.gallery__main', gallery);
      const slides = $$('.gallery__slide', gallery);
      const thumbs = $$('.gallery__thumb', gallery);
      const dots = $$('.gallery__dot', gallery);
      const setCurrent = (idx) => {
        thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === idx)));
        dots.forEach((d, i) => d.setAttribute('aria-current', String(i === idx)));
      };
      const goTo = (slide) => {
        const idx = slides.indexOf(slide);
        if (idx < 0) return;
        if (window.matchMedia('(min-width: 990px)').matches && gallery.classList.contains('gallery--stacked')) {
          slide.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
        } else {
          main.scrollTo({ left: slide.offsetLeft - main.offsetLeft, behavior: reducedMotion ? 'auto' : 'smooth' });
        }
        setCurrent(idx);
      };
      thumbs.forEach((t, i) => t.addEventListener('click', () => goTo(slides[i])));
      dots.forEach((d, i) => d.addEventListener('click', () => goTo(slides[i])));
      main.addEventListener('scroll', debounce(() => {
        const idx = Math.round(main.scrollLeft / main.clientWidth);
        setCurrent(idx);
      }, 60), { passive: true });
      const root = gallery.closest('[data-product]');
      if (root) root._gallery = { goTo };
    });
  };

  /* ---------- Sticky add to cart (mobile) ---------- */
  const initStickyATC = () => {
    const bar = $('[data-sticky-atc]');
    const target = $('[data-add-button]');
    if (!bar || !target || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
    });
    io.observe(target);
    bar.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-sticky-add]');
      if (!btn) return;
      e.preventDefault();
      const form = $('form[data-product-form]');
      if (form) form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event('submit', { cancelable: true }));
    });
  };

  /* ---------- Product recommendations ---------- */
  const initRecommendations = () => {
    $$('[data-recommendations]').forEach(async (el) => {
      if (el._loaded) return;
      el._loaded = true;
      try {
        const res = await fetch(el.dataset.url);
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim().length) {
          el.innerHTML = fresh.innerHTML;
          initReveal(el);
        } else {
          el.closest('.shopify-section')?.setAttribute('hidden', '');
        }
      } catch (_) { /* silent */ }
    });
  };

  /* ---------- Search modal + predictive ---------- */
  const initSearch = () => {
    const modal = $('[data-search-modal]');
    if (!modal) return;
    const input = $('input[type="search"]', modal);
    const results = $('[data-predictive-results]', modal);
    let lastTrigger;
    const open = (trigger) => {
      lastTrigger = trigger;
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      setTimeout(() => input.focus(), 80);
    };
    const close = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      lastTrigger && lastTrigger.focus();
    };
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-search-open]');
      if (opener) { e.preventDefault(); Drawers.close(false); open(opener); return; }
      if (e.target.closest('[data-search-close]')) { e.preventDefault(); close(); }
    });
    modal.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') close();
      else trapFocus($('.search-modal__panel', modal), e);
    });
    if (!results || !theme.routes.predictiveSearch) return;
    let controller;
    input.addEventListener('input', debounce(async () => {
      const q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      controller && controller.abort();
      controller = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search&resources[limit]=8&resources[type]=product,collection,page,article`;
        const res = await fetch(url, { signal: controller.signal });
        const text = await res.text();
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-predictive]');
        results.innerHTML = fresh ? fresh.outerHTML : '';
      } catch (_) { /* aborted */ }
    }, 250));
  };

  /* ---------- Collection sorting / filters ---------- */
  const initFacets = () => {
    document.addEventListener('change', (e) => {
      const sort = e.target.closest('[data-sort]');
      if (sort) {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sort.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      }
    });
  };

  /* ---------- Video (click to load) ---------- */
  const initVideo = () => {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-video-play]');
      if (!btn) return;
      const wrap = btn.closest('.media');
      const tpl = $('template', wrap);
      if (tpl) {
        wrap.appendChild(tpl.content.cloneNode(true));
        btn.remove();
      }
    });
  };

  /* ---------- Boot ---------- */
  const boot = () => {
    Drawers.init();
    Cart.init();
    initAnnouncements();
    initHeader();
    initFaders();
    initReveal();
    initQuantity();
    initProductForms();
    initVariantPickers();
    initGalleries();
    initStickyATC();
    initRecommendations();
    initSearch();
    initFacets();
    initVideo();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    initFaders(e.target);
    initReveal(e.target);
    initProductForms(e.target);
    initVariantPickers(e.target);
    initGalleries(e.target);
    initRecommendations();
    initAnnouncements();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const root = e.target.closest('[data-slideshow], [data-quotes]');
    if (root && root._fader) {
      const idx = root._fader.slides.indexOf(e.target);
      if (idx > -1) { root._fader.pause(); root._fader.go(idx); }
    }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const root = e.target.closest('[data-slideshow], [data-quotes]');
    root && root._fader && root._fader.play();
  });

  window.CNC = { Cart, Drawers, formatMoney, toast };
})();
